import {
    useState,
} from 'react';
import {
    useMutation,
} from '@apollo/client';
import {FieldValues, useForm} from 'react-hook-form';
import {
    Button, Modal,
    SelectBox,
    Textarea,
} from '../../components/core';
import Form from '../../components/core/form/form';
import * as styles from './reportFeedback.module.css';
import toastify from '../../helpers/toast/toastify';
import ScreenshotInput from '../../components/core/form/input/screenshot';
import {
    FeedbackType, ReportFeedbackDocument, UploadScreenshotDocument,
} from '../../graphql-types';
import ScreenshotInvisible from '../../components/core/slot/ScreenshotInvisible';
import resizeImage from '../../helpers/screenshot/resizeImage';
import AnalyticEventValues from '../../configs/hooks/analytics/analyticEventvalues';
import logClick from '../../helpers/analytics/logClick';

interface ReportFeedbackModalProps {
    closeModal: () => void,
}

const ReportFeedbackModal = ({closeModal}: ReportFeedbackModalProps) => {
    const [loading, setLoading] = useState(false);
    const [reportFeedback] = useMutation(ReportFeedbackDocument);
    const [uploadScreenshot] = useMutation(UploadScreenshotDocument);
    const [screenshot, setScreenshot] = useState<string | null>(null);

    const feedbackTypes = [
        {
            id: FeedbackType.Questions,
            name: 'Questions',
        },
        {
            id: FeedbackType.Issues,
            name: 'Issues',
        },
        {
            id: FeedbackType.MissingContent,
            name: 'Missing content',
        },
        {
            id: FeedbackType.FeatureRequest,
            name: 'Feature request',
        },
        {
            id: FeedbackType.GeneralFeedback,
            name: 'General feedback',
        },
    ];

    const formMethods = useForm<FieldValues>({
        mode: 'onChange',
        defaultValues: {
            type: null,
            title: '',
            message: '',
            screenshotUrl: null,
        },
    });

    const {
        control,
        handleSubmit,
        formState: {isValid},
    } = formMethods;

    const onSubmit = async (formData: FieldValues) => {
        setLoading(true);

        let analyticsData = {};

        try {
            const {type, title, message} = formData;
            let screenshotUrl = null;

            if (screenshot) {
                try {
                    const resizedScreenshot = await resizeImage(screenshot, {maxSizeInBytes: 1 * 1024 * 1024});
                    const file = await fetch(resizedScreenshot)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'screenshot.png', {type: 'image/png'}));

                    const uploadResult = await uploadScreenshot({
                        variables: {
                            input: file,
                        },
                        context: {
                            hasUpload: true,
                        },
                    });

                    screenshotUrl = uploadResult.data?.uploadScreenshot.imageUrl || null;
                } catch (error) {
                    toastify({type: 'error', text: `Error uploading screenshot: ${error.message}`});
                }
            }

            const newData = {
                type,
                title,
                message,
                screenshotUrl,
            };

            analyticsData = {
                type,
                title,
                message,
                screenshot: Boolean(screenshot),
                screenshotUrl: Boolean(screenshotUrl),
            };

            await reportFeedback({
                variables: {
                    input: newData,
                },
                onCompleted() {
                    toastify({type: 'success', text: 'Thank you! Your feedback was submitted successfully'});
                    closeModal();
                    analyticsData = {
                        ...analyticsData,
                        success: true,
                    };
                },
                onError(error) {
                    toastify({type: 'error', text: `Error submitting feedback: ${error.message}`});
                    analyticsData = {
                        ...analyticsData,
                        success: false,
                    };
                },
            });
        } catch (error) {
            toastify({type: 'error', text: `Error submitting feedback: ${error.message}`});
        }

        logClick(AnalyticEventValues.feedback, analyticsData);

        setLoading(false);
    };

    return (
        <ScreenshotInvisible>
            <Modal
                title="Help Us Improve — Describe Your Feedback"
                closeModal={closeModal}
                classNames={{
                    modal: styles.modal,
                }}
                buttons={(
                    <Button
                        text="Submit Feedback"
                        loading={loading}
                        disabled={!isValid || loading}
                        size="medium"
                        onClick={handleSubmit(onSubmit)}
                    />
                )}
            >
                <Form methods={formMethods}>
                    <Form.Row>
                        <Form.Column className={styles.formCol}>
                            <SelectBox
                                title="Choose Your Feedback Type"
                                placeholder="Please select one..."
                                control={control}
                                controllerName="type"
                                optionsValues={
                                    feedbackTypes.map(type => ({
                                        id: type?.id,
                                        value: type?.id,
                                        label: type?.name,
                                    }))
                                }
                                canType
                                required
                            />
                        </Form.Column>
                    </Form.Row>
                    <Form.Row>
                        <Form.Column className={styles.formCol}>
                            <Textarea
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: 'Message is required',
                                    },
                                }}
                                name="message"
                                title="Describe Your Feedback"
                                placeholder='Please describe what happened, what you expected, or your idea...'
                            />
                        </Form.Column>
                    </Form.Row>
                    <Form.Row>
                        <Form.Column className={styles.formCol}>
                            <ScreenshotInput title='Attached Screenshot' onChange={setScreenshot} />
                        </Form.Column>
                    </Form.Row>
                </Form>
                <p className={styles.formDisclaimer}>We may email you for more information or updates.</p>
            </Modal>
        </ScreenshotInvisible>
    );
};

export default ReportFeedbackModal;
